'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Flag, Loader2 } from 'lucide-react';
import { useContentFlagsQuery } from '@/api/useContentFlags';

const FLAG_REASONS = [
  'Sexual content',
  'Foul language',
  'Incivility',
  'Discrimination',
  'Personal attacks',
  'Harassment',
  'Hate speech',
  'Threats',
  'Fraud',
  'Spam',
  'Scam',
] as const;

interface FlagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFlag: (reason: string) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

export default function FlagModal({
  isOpen,
  onClose,
  onFlag,
  isLoading = false,
  title = "Flag Content",
  description = "Please provide a reason for flagging this content. This will help our moderation team review the issue."
}: FlagModalProps) {
  const [reason, setReason] = useState('');
  const { data, isLoading: isLoadingFlags } = useContentFlagsQuery({
    module: 'forums',
    status: '0',
    sortBy: 'createdAt',
    order: 'DESC',
    page: 1,
    pageSize: 10,
  })

  const handleSubmit = () => {
    if (reason.trim()) {
      onFlag(reason.trim());
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (isLoadingFlags) {
    return <div>Loading...</div>
  }
  // read data list from data which is pagination list
  const dataList = data?.rows || [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className='md:w-full'>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-500" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-black">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="space-y-2">
            <label htmlFor="reason" className="text-sm font-medium">
              Reason for flagging *
            </label>
            <Select
              value={reason}
              onValueChange={setReason}
              disabled={isLoading}
            >
              <SelectTrigger
                id="reason"
                className="mt-2 w-full border border-input focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              >
                <SelectValue placeholder="Select a reason for flagging..." />
              </SelectTrigger>
              <SelectContent className="border border-input">
                {FLAG_REASONS.map((reasonOption) => (
                  <SelectItem 
                    key={reasonOption} 
                    value={reasonOption}
                    className="focus:bg-accent focus:text-accent-foreground hover:bg-accent/50"
                  >
                    {reasonOption}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!reason.trim() || isLoading}
            className="bg-red-500 hover:bg-red-600"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Flagging...
              </>
            ) : (
              <>
                <Flag className="h-4 w-4 mr-2" />
                Flag
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
